
import { Gait } from "../sim/movement";
import { HEADING_STEPS, cosFx, sinFx, FRAC_ONE, headingDelta, legacyToMm } from "../sim/fixed";

/** How far past the destination a body may be carried before its source is
 *  re-based onto where it actually is and it is re-aimed. `fRestLen < -100.0f`
 *  (`InstanceBase.cpp:1633`), in this build's millimetres --
 *  `MM_PER_LEGACY_UNIT` is 10. */
export const OVERSHOOT_MM = legacyToMm(100);

/** Closer than this to the destination, a stop command snaps rather than walks:
 *  `fDirLen > 1.0f` (`InstanceBase.cpp:1334`). Ten millimetres. */
export const ARRIVAL_MM = legacyToMm(1);

/** How long the whole positional error to the newest server position takes to
 *  be paid out. `SetBlendingPosition`'s default `fBlendingTime`
 *  (`GameLib/ActorInstance.h:364`) -- one second, linear. */
export const SYNC_BLEND_MS = 1_000;

/** `c_fDefaultRotationSpeed` (`InstanceBase.cpp:17`), degrees a second, and the
 *  five-twelfths it drops to for a turn already inside 45 degrees
 *  (`InstanceBaseMovement.cpp:135-143`). */
export const ROTATION_SPEED_DEG_S = 1_200;
export const NEAR_ROTATION_SPEED_DEG_S = (ROTATION_SPEED_DEG_S * 5) / 12;
export const NEAR_ROTATION_DEG = 45;

const STEPS_PER_DEGREE = HEADING_STEPS / 360;

/** How many steps `__Push` walks the straight line to a shove's end in, any of
 *  them on a blocked cell refusing the whole shove (`ActorInstanceSync.cpp:19,30-39`). */
export const PUSH_STEPS = 100;

/** A shove longer than this many whole legacy units knocks its body down
 *  (`ActorInstanceSync.cpp:45-46`). */
export const PUSH_KNOCKDOWN_UNITS = 150;

/** One shove, as `__Push` is handed it: where the body is drawn, where the
 *  server put it, and the three things about the body that change the answer. */
export interface PushInput {
  readonly fromXMm: number;
  readonly fromYMm: number;
  readonly toXMm: number;
  readonly toYMm: number;
  /** `IsResistFallen` (`ActorInstanceSync.cpp:9-10`). */
  readonly resistsFall: boolean;
  /** `IsUsingSkill` (`ActorInstanceSync.cpp:43`). */
  readonly usingSkill: boolean;
  /** `IPhysicsWorld::isPhysicalCollision`: `ATTRIBUTE_BLOCK` at a point (`MapManager.cpp:497-504`). */
  blocked(xMm: number, yMm: number): boolean;
}

/** Whether a shove moves the drawn body, and whether it knocks it down. */
export interface PushOutcome {
  readonly blend: boolean;
  readonly knockDown: boolean;
}

const REFUSED: PushOutcome = { blend: false, knockDown: false };

/** `CActorInstance::__Push` (`ActorInstanceSync.cpp:7-53`): a body that resists
 *  falling is not moved; one whose straight line to the new position crosses a
 *  blocked cell in any of its hundred steps short of the end is not moved either;
 *  any other is blended there, and knocked down when the shove is longer than 150
 *  whole units and it is not mid-skill. */
export function pushOutcome(input: PushInput): PushOutcome {
  if (input.resistsFall) return REFUSED; // ActorInstanceSync.cpp:9-10
  const dxMm = input.toXMm - input.fromXMm;
  const dyMm = input.toYMm - input.fromYMm;
  for (let i = 0; i < PUSH_STEPS; i++) { // ActorInstanceSync.cpp:30-39
    const xMm = input.fromXMm + (dxMm * i) / PUSH_STEPS;
    const yMm = input.fromYMm + (dyMm * i) / PUSH_STEPS;
    if (input.blocked(xMm, yMm)) return REFUSED; // ResetBlendingPosition, :33-37
  }
  if (input.usingSkill) return { blend: true, knockDown: false }; // ActorInstanceSync.cpp:43
  const units = Math.trunc(Math.hypot(dxMm, dyMm) / legacyToMm(1)); // `int len=sqrt(...)`, ActorInstanceSync.cpp:45
  return { blend: true, knockDown: units > PUSH_KNOCKDOWN_UNITS };
}

/** `CEaseOutInterpolation` from 0 to 1 over the unit interval (`MapUtil.cpp:259-291`):
 *  `Setup` starts the speed at twice the mean and decelerates it to rest at the
 *  end, which is `2u - u²` in closed form. */
export function easeOut(u: number): number {
  const t = Math.min(1, Math.max(0, u));
  return t * (2 - t);
}

/** One sample of one remote body, as the server described it. */
export interface MotionSample {
  timeMs: number;
  xMm: number;
  yMm: number;
  heading: number;
  gait: Gait;
}

/** The drawn state of one remote body. */
export interface DrawnMotion {
  xMm: number;
  yMm: number;
  heading: number;
  gait: Gait;
}

export class RemoteMotion {
  /** Where it is drawn. */
  xMm = 0;
  yMm = 0;
  /** Where it was when the current destination was set --
   *  `NEW_SetSrcPixelPosition`. */
  private srcXMm = 0;
  private srcYMm = 0;
  /** The newest position the server reported -- `NEW_SetDstPixelPosition`. */
  private dstXMm = 0;
  private dstYMm = 0;
  /** `m_fAdvancingRotation`: the direction src -> dst, in heading steps. */
  private advancing = 0;
  /** `m_GraphicThingInstance.GetRotation()`: the facing actually drawn, which
   *  turns toward `advancing` rather than snapping to it. */
  heading = 0;
  /** `m_isGoing`. */
  private going = false;
  /** `m_kMovAfterFunc.eFunc == FUNC_WAIT`: stop on arrival rather than walking
   *  through. */
  private stopOnArrival = false;
  /** The body's own motion speed, mm/s -- see the module doc. Retained across a
   *  stop command so that the walk to the final destination happens at the
   *  speed the body was moving at, which is what `EndWalking()` on arrival
   *  means. */
  private speedMmS = 0;
  /** The gait to draw while `going`. A body still walking to the destination
   *  after a stop command has moving legs until it gets there. */
  private movingGait: Gait = Gait.RUN;
  private placed = false;
  /** `CPhysicsObject`'s pushing blend (`PhysicsObject.cpp:110-117`): where a
   *  shove started, where it ends, and how long it has run; negative when no
   *  shove is running. */
  private pushFromXMm = 0;
  private pushFromYMm = 0;
  private pushToXMm = 0;
  private pushToYMm = 0;
  private pushElapsedMs = -1;
  /** The newest server sample a running shove held back: `__IsSyncing` refuses
   *  a move while the blend runs (`InstanceBaseMovement.cpp:32,253,295`). */
  private held: MotionSample | null = null;

  /** `IsPushing` (`ActorInstanceSync.cpp:73-76`). */
  get pushing(): boolean {
    return this.pushElapsedMs >= 0;
  }

  /** `SetBlendingPosition` with its default time (`ActorInstanceBattle.cpp:888-898`,
   *  `ActorInstance.h:364`): ease the drawn body from where it is to the shove's
   *  end over `SYNC_BLEND_MS`. A body not yet placed has nowhere to be shoved from. */
  push(toXMm: number, toYMm: number): void {
    if (!this.placed) return;
    this.pushFromXMm = this.xMm;
    this.pushFromYMm = this.yMm;
    this.pushToXMm = toXMm;
    this.pushToYMm = toYMm;
    this.pushElapsedMs = 0;
    this.going = false;
    this.srcXMm = toXMm;
    this.srcYMm = toYMm;
    this.dstXMm = toXMm;
    this.dstYMm = toYMm;
  }

  /** `ResetBlendingPosition` (`ActorInstanceBattle.cpp:900-903`). */
  resetPush(): void {
    this.pushElapsedMs = -1;
  }

  /** Fold in one server sample. `samples` is the entity's trail, newest last,
   *  including this one -- the speed is measured off it (module doc). */
  observe(sample: MotionSample, samples: readonly MotionSample[]): void {
    const measured = measuredSpeedMmS(samples);
    if (measured > 0) this.speedMmS = measured;
    if (this.pushing) {
      this.held = sample;
      return;
    }

    if (!this.placed) {
      // First sight: the body arrives where the server put it. There is no
      // previous drawn position for it to walk from.
      this.placed = true;
      this.xMm = sample.xMm;
      this.yMm = sample.yMm;
      this.heading = sample.heading;
      this.advancing = sample.heading;
      this.srcXMm = sample.xMm;
      this.srcYMm = sample.yMm;
      this.dstXMm = sample.xMm;
      this.dstYMm = sample.yMm;
      this.going = false;
      this.stopOnArrival = true;
      if (sample.gait !== Gait.IDLE) this.movingGait = sample.gait;
      return;
    }

    if (sample.gait !== Gait.IDLE) this.movingGait = sample.gait;

    const dx = sample.xMm - this.xMm;
    const dy = sample.yMm - this.yMm;
    const away = Math.hypot(dx, dy);

    // `StateProcess`'s `FUNC_WAIT` arm (`InstanceBase.cpp:1331-1367`): a body
    // that has stopped and is already there stops where it is told to, now.
    if (sample.gait === Gait.IDLE && away <= ARRIVAL_MM) {
      this.xMm = sample.xMm;
      this.yMm = sample.yMm;
      this.srcXMm = sample.xMm;
      this.srcYMm = sample.yMm;
      this.dstXMm = sample.xMm;
      this.dstYMm = sample.yMm;
      this.advancing = sample.heading;
      this.going = false;
      return;
    }

    this.srcXMm = this.xMm;
    this.srcYMm = this.yMm;
    this.dstXMm = sample.xMm;
    this.dstYMm = sample.yMm;
    // `SetAdvancingRotation(NEW_GetAdvancingRotationFromPixelPosition(src,
    // dst))`. Under the arrival epsilon there is no direction in the pair to
    // read, and the original takes the command's own rotation instead
    // (`:1360-1362`).
    this.advancing = away > ARRIVAL_MM ? headingTo(dx, dy) : sample.heading;
    this.going = true;
    this.stopOnArrival = sample.gait === Gait.IDLE;
  }

  /** A crossing, a respawn, a warp: the body arrives rather than sprinting. */
  snapTo(sample: MotionSample): void {
    this.placed = false;
    this.observe(sample, [sample]);
  }

  advance(dtMs: number, into: DrawnMotion): DrawnMotion {
    if (dtMs > 0 && this.placed && this.pushing) {
      this.pushElapsedMs += dtMs;
      const eased = easeOut(this.pushElapsedMs / SYNC_BLEND_MS);
      this.xMm = this.pushFromXMm + (this.pushToXMm - this.pushFromXMm) * eased;
      this.yMm = this.pushFromYMm + (this.pushToYMm - this.pushFromYMm) * eased;
      if (this.pushElapsedMs >= SYNC_BLEND_MS) {
        this.pushElapsedMs = -1;
        const held = this.held;
        this.held = null;
        if (held !== null) this.observe(held, [held]);
      }
    } else if (dtMs > 0 && this.placed) {
      // 1. Dead reckoning -- `AccumulationMovement`.
      if (this.going && this.speedMmS > 0) {
        const distance = (this.speedMmS * dtMs) / 1000;
        this.xMm += (distance * cosFx(this.advancing)) / FRAC_ONE;
        this.yMm += (distance * sinFx(this.advancing)) / FRAC_ONE;
      }

      // 2. `MovementProcess`: overshoot, then arrival.
      if (this.going) {
        const curLen = Math.hypot(this.xMm - this.srcXMm, this.yMm - this.srcYMm);
        const totalLen = Math.hypot(this.dstXMm - this.srcXMm, this.dstYMm - this.srcYMm);
        const restLen = totalLen - curLen;
        if (restLen < -OVERSHOOT_MM) {
          // "If the latency was bad enough that it moved too far"
          // (`InstanceBase.cpp:1632-1646`): re-base and re-aim.
          this.srcXMm = this.xMm;
          this.srcYMm = this.yMm;
          const dx = this.dstXMm - this.xMm;
          const dy = this.dstYMm - this.yMm;
          if (Math.hypot(dx, dy) > ARRIVAL_MM) this.advancing = headingTo(dx, dy);
          // "Make it stop next time round" -- a body already past where it was
          // last seen has nowhere further to go on its own.
          this.stopOnArrival = true;
        } else if (restLen <= 0 && this.stopOnArrival) {
          // The `default:` arm of the arrival switch (`:1738-1755`):
          // `SCRIPT_SetPixelPosition(dst)`, `EndWalking()`.
          this.xMm = this.dstXMm;
          this.yMm = this.dstYMm;
          this.going = false;
        }
      }

      const closed = Math.min(1, dtMs / SYNC_BLEND_MS);
      this.xMm += (this.dstXMm - this.xMm) * closed;
      this.yMm += (this.dstYMm - this.yMm) * closed;

      // 4. Rotation -- `MovementProcess`'s tail (`:1766-1790`).
      this.heading = turnToward(this.heading, this.advancing, dtMs);
    }

    into.xMm = this.xMm;
    into.yMm = this.yMm;
    into.heading = this.heading;
    into.gait = this.going ? this.movingGait : Gait.IDLE;
    return into;
  }
}

/** The direction of a displacement, as a 12-bit heading step. */
function headingTo(dx: number, dy: number): number {
  const steps = (Math.atan2(dy, dx) / (Math.PI * 2)) * HEADING_STEPS;
  const wrapped = steps % HEADING_STEPS;
  return wrapped < 0 ? wrapped + HEADING_STEPS : wrapped;
}

/** Turn `from` toward `to` by at most this frame's allowance, the short way. */
export function turnToward(from: number, to: number, dtMs: number): number {
  const delta = headingDelta(from, to);
  const degrees = Math.abs(delta) / STEPS_PER_DEGREE;
  const speed = degrees > NEAR_ROTATION_DEG ? ROTATION_SPEED_DEG_S : NEAR_ROTATION_SPEED_DEG_S;
  const allowed = (speed * STEPS_PER_DEGREE * dtMs) / 1000;
  const step = Math.abs(delta) <= allowed ? delta : Math.sign(delta) * allowed;
  const turned = (from + step) % HEADING_STEPS;
  return turned < 0 ? turned + HEADING_STEPS : turned;
}

export function measuredSpeedMmS(samples: readonly MotionSample[]): number {
  let distance = 0;
  let elapsed = 0;
  for (let i = samples.length - 1; i > 0; i--) {
    const b = samples[i];
    const a = samples[i - 1];
    if (b.gait === Gait.IDLE || a.gait === Gait.IDLE) break;
    const span = b.timeMs - a.timeMs;
    if (span <= 0) break;
    distance += Math.hypot(b.xMm - a.xMm, b.yMm - a.yMm);
    elapsed += span;
  }
  return elapsed > 0 ? (distance * 1000) / elapsed : 0;
}
